import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert, EmptyState, PageHeader, ScoreBar, Spinner } from '@aigovops/design-system';
import { useRequiredProfile } from '../lib/auth';
import { useOptionalWorkspace } from '../lib/workspace';
import { getAssessment } from '../lib/api';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';

export function AttestedCapabilities() {
  const profileId = useRequiredProfile();
  const workspaceCtx = useOptionalWorkspace();
  const prefix = workspaceCtx ? `/w/${workspaceCtx.workspace.slug}/app` : '/app'; // scope-check-ignore: legacy fallback
  const assess = useWorkspaceQuery((s) => queryKeys.capabilities(s, profileId), () => getAssessment(profileId));

  const capabilityRows = useMemo(() => {
    if (!assess.data) return [];
    return assess.data.framework_scores.map((fs) => ({
      framework_id: fs.framework_id,
      framework_name: fs.framework_name,
      applicable: fs.applicable_items,
      complete: fs.complete_count,
      score: fs.score,
      effectiveLevel: Math.min(5, Math.max(1, Math.ceil(fs.score / 20))),
    }));
  }, [assess.data]);

  if (assess.loading) return <Spinner label="Loading capabilities" />;
  if (assess.error) return <Alert variant="error">{assess.error}</Alert>;
  if (!assess.data) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Attested capabilities"
        subtitle="Maturity levels by framework"
      />

      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
        Maturity scale: L1 Ad hoc · L2 Documented · L3 Managed · L4 Measured · L5 Optimized
      </div>

      {capabilityRows.length === 0 ? (
        <EmptyState
          title="No capabilities assessed yet"
          description="Run a readiness assessment to see your attested capability levels."
        />
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)',
                color: 'var(--text)',
                borderCollapse: 'collapse',
              }}
            >
              <thead style={{ background: 'var(--surface-elevated)', color: 'var(--text-dim)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Framework</th>
                  <th style={{ textAlign: 'center', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Required level</th>
                  <th style={{ textAlign: 'center', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Achieved level</th>
                  <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Coverage</th>
                  <th style={{ textAlign: 'right', padding: 'var(--space-2) var(--space-3)', fontWeight: 500, fontSize: 'var(--font-size-3xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {capabilityRows.map((row) => (
                  <tr key={row.framework_id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: 'var(--space-2) var(--space-3)', fontWeight: 500 }}>
                      <Link
                        to={`${prefix}/register?framework=${row.framework_id}`}
                        style={{ color: 'var(--text)', textDecoration: 'underline', textDecorationColor: 'var(--border-strong)' }}
                      >
                        {row.framework_name}
                      </Link>
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'center' }}>
                      <LevelPips target={0} achieved={0} />
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'center' }}>
                      <LevelPips target={0} achieved={row.effectiveLevel} />
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)', minWidth: '100px' }}>
                      <ScoreBar score={row.score} />
                      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                        {row.complete} of {row.applicable}
                      </div>
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                      {row.score}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function LevelPips({ target, achieved }: { target: number; achieved: number }) {
  return (
    <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
      {[1, 2, 3, 4, 5].map((lvl) => (
        <div
          key={lvl}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: lvl <= achieved
              ? 'var(--success)'
              : lvl <= target
                ? 'var(--border-strong)'
                : 'var(--border)',
            position: 'relative',
          }}
          title={`Level ${lvl}`}
        >
          <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            {lvl <= achieved ? `Level ${lvl}: achieved` : lvl <= target ? `Level ${lvl}: target` : `Level ${lvl}: not set`}
          </span>
        </div>
      ))}
    </div>
  );
}
